<template>
  <div class="counter" :style="getCounterStyle">
    <div class="counter__score">{{ item.score }}</div>
    <div class="counter__body">
      <div class="counter__city">{{ item.city }}</div>
      <div class="counter__region">{{ item.region }}</div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'SummaryInfoBox',

  props: {
    delay: {
      type: Number,
      required: false,
      default: 100,
    },
  },

  data() {
    return {
      item: {
        score: 0,
        city: '',
        region: '',
        color: 'white',
      },
      count: 0,
      prevCount: 0,
      useTimer: true,
      timeout: null,
      currentStateIndex: -1,
      states: {
        '0,25000': 'small',
        '25000,50000': 'medium',
        '50000,100000': 'normal',
        '100000,20000000': 'large',
      },
      stateLabels: {
        '0,25000': '< 25.000',
        '25000,50000': '25.000 - 50.000',
        '50000,100000': '50.000 - 100.000',
        '100000,20000000': '> 100.000',
      },
    };
  },

  computed: {
    ...mapState({
      counterIsRunning: (state) => state.infoBoxCounter.isRunning,
      continueToNextState: (state) => state.infoBoxCounter.continueToNextState,
      selectedYear: (state) => state.rawData.year,
      // clickedRegion: (state) => state.dataLayer.selected,
      // selectedRegion: (state) => state.dataLayer.active,
      // featureHover: (state) => state.dataLayer.hover,
      // featureClicked: (state) => state.dataLayer.clicked,
    }),

    ...mapGetters({
      items: 'rawData/separatedData',
      statedFilters: 'filter/filters',
    }),

    state() {
      return this.states[this.statedFilters.Inwoners?.value[0]];
    },

    list() {
      if (this.items) {
        return this.items[this.state];
      }
      return [];
    },

    setStyle() {
      return `background-color: ${this.item.color};`;
    },

    getCounterStyle() {
      let visibility = 'hidden';
      if (this.counterIsRunning && this.count > 0) {
        visibility = 'visible';
      }
      return `visibility: ${visibility}`;
    },
  },

  mounted() {
    this.timeout = setTimeout(this.loop, this.delay);

    window.addEventListener('beforeunload', () => {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    });
  },

  watch: {
    counterIsRunning(isRunning) {
      if (isRunning) {
        this.count = this.prevCount;
        this.useTimer = true;
        this.prevCount = 0;

        this.timeout = setTimeout(this.loop, 100);
      } else {
        this.$store.commit('infoBoxCounter/setActiveItem', null);
        clearTimeout(this.timeout);
      }
    },

    selectedYear() {
      clearTimeout(this.timeout);
      this.count = 0;

      if (!this.counterIsRunning) {
        this.useTimer = false;
      }

      if (!this.stopped) {
        this.timeout = setTimeout(this.loop, 0);
      }
    },

    statedFilters() {
      clearTimeout(this.timeout);
      this.count = 0;

      if (!this.counterIsRunning) {
        this.useTimer = false;
      }

      if (!this.stopped) {
        this.timeout = setTimeout(this.loop, 0);
      }
    },

    list() {
      if (this.list) {
        this.count = this.list.length;
      }
    },
  },

  methods: {
    loop() {
      if (this.list) {
        this.count -= 1;

        if (this.count < 0) {
          if (this.continueToNextState) {
            // show top item during state change
            this.count = 0;
            this.setSpecs();

            // select next state
            this.currentStateIndex += 1;
            const state = Object.keys(this.states)[this.currentStateIndex];
            const label = this.stateLabels[state];
            if (state) {
              this.$store.commit('filter/changed', {
                key: 'Inwoners',
                type: 'RANGE',
                label,
                value: [state],
              });
            } else {
              // stop counter after last state
              clearTimeout(this.timeout);
              this.$store.commit('infoBoxCounter/stop');
              this.$store.commit('infoBoxCounter/setActiveItem', null);
            }
          } else {
            // stop counter when ready
            clearTimeout(this.timeout);
            this.$store.commit('infoBoxCounter/stop');
            this.$store.commit('infoBoxCounter/setActiveItem', null);
          }
        } else {
          this.setSpecs();
        }
      }
    },

    setSpecs() {
      if (!this.useTimer) {
        this.$store.commit('infoBoxCounter/setActiveItem', this.list);

        return;
      }

      const listItem = this.list[this.count];

      this.$store.commit('infoBoxCounter/setActiveItem', listItem);

      if (listItem) {
        this.item.score = listItem.Ranking;
        this.item.city = listItem.Gemeente;
        this.item.region = listItem.Provincie;
        this.item.color = listItem.colors;
      }

      if (this.useTimer) {
        const delay = this.delay * (this.count / this.list.length);
        this.timeout = setTimeout(this.loop, Math.max(delay, 20));
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .counter {
    align-items: center;
    align-self: top;
    background-color: $color-white;
    border-radius: $global-radius;
    box-shadow: 0 2px 6px rgba($color-silver, .5);
    display: flex;
    min-width: 300px;
    overflow: hidden;

    @include mq($until: tablet) {
      min-width: 250px;
    }

    &__score {
      @include font-size(jumbo, 48px);

      align-items: center;
      align-self: stretch;
      background-color: $color-cerulean;
      color: $color-white;
      display: flex;
      font-weight: bold;
      justify-content: center;
      min-height: 70px;
      min-width: 70px;
      padding-left: $global-spacing-unit-half;
      padding-right: $global-spacing-unit-half;

      @include mq($until: tablet) {
        min-height: 60px;
      }
    }

    &__body {
      @include font-size(micro, 12px);

      // min-width: 115px;
      padding: $global-spacing-unit-half;
      padding-right: $global-spacing-unit;
    }

    &__city {
      @include font-size(delta-epsilon, 22px);

      color: $color-black;
      white-space: nowrap;
    }

    &__region {
      @include font-size(milli, 16px);

      color: $color-black;
    }
  }
</style>
